import type {
  ModuleDefinition,
  UndeadUnitDefinition,
} from "./phase-three-types";
import { getUndeadUnit } from "./undead-units";

export interface DpsBand {
  baseline: number;
  min: number;
  max: number;
}

export const EXTREMELY_LOW_DAMAGE_SKILL_WEIGHT_MAX = 25;

export const NORMAL_DPS_BANDS: Readonly<Record<1 | 2 | 3 | 4 | 5, DpsBand>> = {
  1: { baseline: 10, min: 8, max: 13 },
  2: { baseline: 40, min: 35, max: 50 },
  3: { baseline: 100, min: 90, max: 120 },
  4: { baseline: 200, min: 180, max: 240 },
  5: { baseline: 400, min: 370, max: 450 },
};

export const EXCEPTIONAL_DPS_BANDS: Readonly<
  Record<1 | 2 | 3 | 4 | 5, DpsBand>
> = {
  1: { baseline: 22.5, min: 20, max: 25 },
  2: { baseline: 90, min: 80, max: 100 },
  3: { baseline: 225, min: 200, max: 250 },
  4: { baseline: 450, min: 400, max: 500 },
  5: { baseline: 760, min: 720, max: 800 },
};

export function getModulePaperDps(
  module: ModuleDefinition,
  undeadUnits?: readonly UndeadUnitDefinition[],
): number {
  const combatDps = module.combat
    ? (((module.combat.damage * (module.combat.hits ?? 1) +
          (module.combat.delayedEcho?.damage ?? 0)) *
        (module.combat.projectiles ?? 1) *
        (1 +
          (module.combat.doubleEveryAttacks
            ? 1 / module.combat.doubleEveryAttacks
            : 0)) *
        1_000) /
      module.combat.intervalMs)
    : 0;
  const companionDps =
    module.companion?.damage !== undefined &&
    module.companion.intervalMs !== undefined
      ? ((module.companion.damage *
          module.companion.count *
          1_000) /
        module.companion.intervalMs)
      : 0;
  const undeadSummon = module.mechanics?.find(
    (mechanic) => mechanic.kind === "undead_summon",
  );
  const undeadDps =
    undeadSummon?.kind === "undead_summon"
      ? (() => {
          const unit = undeadUnits
            ? undeadUnits.find(
                (candidate) => candidate.id === undeadSummon.unitId,
              )
            : getUndeadUnit(undeadSummon.unitId);
          return unit ? (unit.damage * 1_000) / unit.intervalMs : 0;
        })()
      : 0;
  const guardAreaDps = (module.mechanics ?? []).reduce((sum, mechanic) => {
    if (mechanic.kind === "guard_periodic_area_damage") {
      return sum + (mechanic.damage * 1_000) / mechanic.intervalMs;
    }
    if (mechanic.kind === "follower_periodic_damage") {
      return (
        sum +
        (mechanic.damage * mechanic.hits * 1_000) / mechanic.intervalMs
      );
    }
    if (
      mechanic.kind === "periodic_self_area_damage" ||
      mechanic.kind === "follower_periodic_area_damage"
    ) {
      return sum + (mechanic.damage * 1_000) / mechanic.intervalMs;
    }
    if (mechanic.kind === "periodic_health_extremes_damage") {
      return (
        sum +
        ((mechanic.highestDamage + mechanic.lowestDamage) * 1_000) /
          mechanic.intervalMs
      );
    }
    if (mechanic.kind === "basic_attack_chain") {
      const total =
        mechanic.targets * mechanic.initialDamage -
        mechanic.damageLossPerBounce *
          ((mechanic.targets - 1) * mechanic.targets) /
          2;
      return sum + Math.max(0, total);
    }
    if (mechanic.kind === "guard_charge") {
      return sum + (mechanic.damage * 1_000) / mechanic.intervalMs;
    }
    if (mechanic.kind === "movement_damage_field") {
      return sum + (mechanic.damage * 1_000) / mechanic.tickIntervalMs;
    }
    return sum;
  }, 0);
  return combatDps + companionDps + undeadDps + guardAreaDps;
}

export function isDamagingModule(
  module: ModuleDefinition,
  undeadUnits?: readonly UndeadUnitDefinition[],
): boolean {
  return getModulePaperDps(module, undeadUnits) > 0;
}

export function isExceptionalDamageSkill(module: ModuleDefinition): boolean {
  return Boolean(
    module.tier <= 5 &&
      (module.hidden ||
        (module.weight > 0 &&
          module.weight <= EXTREMELY_LOW_DAMAGE_SKILL_WEIGHT_MAX)),
  );
}

export function getTierOneToFiveDpsBand(
  module: ModuleDefinition,
  undeadUnits?: readonly UndeadUnitDefinition[],
): DpsBand | undefined {
  if (
    module.tier > 5 ||
    !isDamagingModule(module, undeadUnits) ||
    isDpsBandExempt(module)
  ) {
    return undefined;
  }
  const tier = module.tier as 1 | 2 | 3 | 4 | 5;
  return isExceptionalDamageSkill(module)
    ? EXCEPTIONAL_DPS_BANDS[tier]
    : NORMAL_DPS_BANDS[tier];
}

export function isDpsBandExempt(module: ModuleDefinition): boolean {
  return (
    module.tags.includes("dps_floor_exception") ||
    module.tags.includes("conditional_reward")
  );
}

export function isAscensionDpsExempt(module: ModuleDefinition): boolean {
  return module.tags.includes("ascension_dps_exception");
}

export function getDesiredTierOneToFiveDps(
  module: ModuleDefinition,
  undeadUnits?: readonly UndeadUnitDefinition[],
): number | undefined {
  const band = getTierOneToFiveDpsBand(module, undeadUnits);
  if (!band) {
    return undefined;
  }
  if (isExceptionalDamageSkill(module)) {
    if (module.combat?.target === "random_area" || module.category === "area") {
      return band.min;
    }
    if (
      module.combat?.target === "elite_only" ||
      module.combat?.target === "boss_only"
    ) {
      return band.max;
    }
    if (module.companion) {
      return band.baseline;
    }
    return band.max;
  }

  let multiplier = 1;
  const combat = module.combat;
  const companion = module.companion;
  if (combat?.target === "random_area" || module.category === "area") {
    multiplier -= 0.08;
  }
  if (combat?.target === "elite_only") {
    multiplier += 0.15;
  }
  if (combat?.target === "boss_only") {
    multiplier += 0.2;
  }
  if ((combat?.chainTargets ?? 0) > 0) {
    multiplier -= 0.03;
  }
  if ((combat?.pierceTargets ?? 0) > 0) {
    multiplier -= 0.03;
  }
  if ((combat?.slowRatio ?? 0) > 0) {
    multiplier -= 0.04;
  }
  if ((combat?.manaCost ?? 0) > 0) {
    multiplier += 0.05;
  }
  if ((combat?.executeNormalEveryMs ?? 0) > 0) {
    multiplier += 0.08;
  }
  const intervalMs = combat?.intervalMs ?? companion?.intervalMs;
  if (
    intervalMs !== undefined &&
    (intervalMs <= 350 || intervalMs >= 2_000)
  ) {
    multiplier -= 0.03;
  }
  if (combat && combat.range >= 800) {
    multiplier -= 0.03;
  }
  if (companion) {
    multiplier -=
      companion.kind === "follower"
        ? 0.05
        : companion.kind === "summon"
          ? 0.08
          : companion.kind === "guard"
            ? 0.1
            : 0.06;
    if ((companion.manaCost ?? 0) > 0) {
      multiplier += 0.05;
    }
  }
  return clamp(band.baseline * multiplier, band.min, band.max);
}

export function isDpsWithinBand(
  dps: number,
  min: number,
  max: number,
): boolean {
  const epsilon = 1e-9;
  return dps >= min - epsilon && dps <= max + epsilon;
}

export function normalizeModuleCatalogDps(
  modules: readonly ModuleDefinition[],
): ModuleDefinition[] {
  const originalDps = new Map(
    modules.map((module) => [module.id, getModulePaperDps(module)]),
  );
  let normalized = modules.map((module) => {
    const band = getTierOneToFiveDpsBand(module);
    const desired = getDesiredTierOneToFiveDps(module);
    return band && desired !== undefined
      ? normalizeModulePaperDps(module, desired, band.min, band.max)
      : cloneModule(module);
  });

  const byId = new Map(normalized.map((module) => [module.id, module]));
  normalized = normalized.map((module) => {
    if (
      module.tier !== 6 ||
      !isDamagingModule(module) ||
      isAscensionDpsExempt(module)
    ) {
      return module;
    }
    const fifth = module.evolutionFrom
      ? byId.get(module.evolutionFrom)
      : undefined;
    if (!fifth || !isDamagingModule(fifth)) {
      return module;
    }
    const originalFifthDps = originalDps.get(fifth.id) ?? 0;
    const originalSixthDps = originalDps.get(module.id) ?? 0;
    const originalRatio =
      originalFifthDps > 0 ? originalSixthDps / originalFifthDps : 1.85;
    const desiredRatio = clamp(originalRatio, 1.7, 2);
    const fifthDps = getModulePaperDps(fifth);
    return normalizeModulePaperDps(
      module,
      fifthDps * desiredRatio,
      fifthDps * 1.7,
      fifthDps * 2,
    );
  });
  return normalized;
}

export function getModuleDpsErrors(
  module: ModuleDefinition,
  modules: readonly ModuleDefinition[],
  undeadUnits?: readonly UndeadUnitDefinition[],
): string[] {
  const dps = getModulePaperDps(module, undeadUnits);
  if (!Number.isFinite(dps) || dps <= 0) {
    return [];
  }
  if (isDpsBandExempt(module)) {
    return [];
  }
  if (module.tier === 6 && isAscensionDpsExempt(module)) {
    return [];
  }
  if (module.tier <= 5) {
    const band = getTierOneToFiveDpsBand(module, undeadUnits)!;
    if (!isDpsWithinBand(dps, band.min, band.max)) {
      const kind = isExceptionalDamageSkill(module)
        ? "隐藏/极低概率"
        : "常规";
      return [
        `${kind}${module.tier}阶伤害技能纸面DPS必须位于${band.min}–${band.max}，当前为${formatDps(dps)}。`,
      ];
    }
    return [];
  }

  const fifth = module.evolutionFrom
    ? modules.find((candidate) => candidate.id === module.evolutionFrom)
    : undefined;
  if (!fifth || !isDamagingModule(fifth, undeadUnits)) {
    return ["六阶伤害技能必须对应一个具有伤害的五阶技能。"];
  }
  const fifthDps = getModulePaperDps(fifth, undeadUnits);
  const ratio = dps / fifthDps;
  if (!isDpsWithinBand(ratio, 1.7, 2)) {
    return [
      `六阶伤害技能纸面DPS必须为对应五阶的170%–200%，当前为${formatDps(ratio * 100)}%。`,
    ];
  }
  return [];
}

function normalizeModulePaperDps(
  module: ModuleDefinition,
  desiredDps: number,
  minDps: number,
  maxDps: number,
): ModuleDefinition {
  const sources: Array<{
    kind: "combat" | "companion";
    damage: number;
    coefficient: number;
  }> = [];
  if (module.combat && module.combat.damage > 0) {
    sources.push({
      kind: "combat",
      damage: module.combat.damage,
      coefficient:
        ((module.combat.hits ?? 1) *
          (module.combat.projectiles ?? 1) *
          1_000) /
        module.combat.intervalMs,
    });
  }
  if (
    module.companion?.damage !== undefined &&
    module.companion.damage > 0 &&
    module.companion.intervalMs !== undefined
  ) {
    sources.push({
      kind: "companion",
      damage: module.companion.damage,
      coefficient:
        (module.companion.count * 1_000) /
        module.companion.intervalMs,
    });
  }
  if (sources.length === 0) {
    return cloneModule(module);
  }

  const currentDps = sources.reduce(
    (sum, source) => sum + source.damage * source.coefficient,
    0,
  );
  const scale = desiredDps / currentDps;
  const candidates = sources.map((source) => {
    const ideal = source.damage * scale;
    const values: number[] = [];
    const start = Math.max(1, Math.floor(ideal) - 6);
    const end = Math.max(start, Math.ceil(ideal) + 6);
    for (let damage = start; damage <= end; damage += 1) {
      values.push(damage);
    }
    return values;
  });

  let bestDamages = sources.map((source) => source.damage);
  let bestScore = Number.POSITIVE_INFINITY;
  const evaluate = (damages: number[]): void => {
    const dps = damages.reduce(
      (sum, damage, index) =>
        sum + damage * sources[index].coefficient,
      0,
    );
    const outsideDistance =
      dps < minDps ? minDps - dps : dps > maxDps ? dps - maxDps : 0;
    const score = outsideDistance * 1_000_000 + Math.abs(dps - desiredDps);
    if (score < bestScore) {
      bestScore = score;
      bestDamages = damages;
    }
  };
  if (candidates.length === 1) {
    for (const first of candidates[0]) {
      evaluate([first]);
    }
  } else {
    for (const first of candidates[0]) {
      for (const second of candidates[1]) {
        evaluate([first, second]);
      }
    }
  }

  let combat = module.combat ? { ...module.combat } : undefined;
  let companion = module.companion ? { ...module.companion } : undefined;
  for (const [index, source] of sources.entries()) {
    if (source.kind === "combat" && combat) {
      combat.damage = bestDamages[index];
    }
    if (source.kind === "companion" && companion) {
      companion.damage = bestDamages[index];
    }
  }
  return {
    ...module,
    bonuses: { ...module.bonuses },
    combat,
    companion,
  };
}

function cloneModule(module: ModuleDefinition): ModuleDefinition {
  return {
    ...module,
    tags: [...module.tags],
    bonuses: { ...module.bonuses },
    combat: module.combat ? { ...module.combat } : undefined,
    companion: module.companion ? { ...module.companion } : undefined,
    economy: module.economy ? { ...module.economy } : undefined,
    growth: module.growth ? { ...module.growth } : undefined,
    onAcquire: module.onAcquire ? { ...module.onAcquire } : undefined,
    healing: module.healing ? { ...module.healing } : undefined,
    archaeology: module.archaeology
      ? { ...module.archaeology }
      : undefined,
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function formatDps(value: number): string {
  return String(Math.round(value * 100) / 100);
}
